/**
 * primitives.ts — the approved primitive registry (the SSOT the component
 * library calls "primitive-definition registry · 15 components").
 *
 * A primitive is method-grounded, never decorative: each carries a method family,
 * construct targets, a cognitive-load budget, validity risks, the shape of evidence
 * it emits, and the review affordances it must offer. `widget` names the React
 * interaction component that renders it; `interactionShape` is the canonical class.
 *
 * Rule (egae-common-rules): "A component without a construct target is
 * inadmissible. A rating scale without an interpretation boundary creates false
 * precision." Every primitive here declares both.
 */
import type { InteractionShape, LoadLevel } from "./types.ts";
import { urn } from "./urn.ts";

/** Which interaction component renders this primitive. Ten cover all sixteen. */
export type WidgetKind =
  | "narrative"
  | "incident"
  | "bestworst"
  | "triad"
  | "ladder"
  | "differential"
  | "scenario"
  | "cardsort"
  | "qsort"
  | "steps"
  | "workload"
  | "canvas";

export interface PrimitiveDefinition {
  readonly primitiveId: string;
  readonly slug: string;
  readonly name: string;
  readonly methodFamily: readonly string[];
  readonly constructTargets: readonly string[];
  readonly interactionShape: InteractionShape;
  readonly widget: WidgetKind;
  readonly bestFor: readonly string[];
  readonly avoidWhen: readonly string[];
  readonly cognitiveLoadBudget: {
    readonly expectedDemand: LoadLevel;
    readonly mitigations: readonly string[];
  };
  readonly validityRisks: readonly string[];
  readonly evidenceOutput: readonly string[];
  readonly reviewAffordance: readonly string[];
  /** The opening prompt the instrument shows the participant. */
  readonly prompt: string;
}

function def(p: Omit<PrimitiveDefinition, "primitiveId">): PrimitiveDefinition {
  return { primitiveId: urn("primitive", p.slug), ...p };
}

export const PRIMITIVES: readonly PrimitiveDefinition[] = [
  def({
    slug: "open-narrative-prompt",
    name: "Open narrative prompt",
    methodFamily: ["Motivational Interviewing", "Narrative"],
    constructTargets: ["operative-values", "incident"],
    interactionShape: "narrative",
    widget: "narrative",
    bestFor: ["surfacing what actually happened in the participant's own words"],
    avoidWhen: ["the participant is fatigued and recall is costly"],
    cognitiveLoadBudget: { expectedDemand: "low", mitigations: ["one open question", "no scaffolding"] },
    validityRisks: ["social-desirability framing", "story smoothing"],
    evidenceOutput: ["narrative span", "reflective summary"],
    reviewAffordance: ["confirm", "correct", "reframe", "redact"],
    prompt: "Tell me about a time the right call wasn't the easy call. What happened?",
  }),
  def({
    slug: "reflective-summary",
    name: "Reflective summary",
    methodFamily: ["Motivational Interviewing"],
    constructTargets: ["meaning-confirmation"],
    interactionShape: "confirmation",
    widget: "narrative",
    bestFor: ["confirming the AI heard the participant before recording meaning"],
    avoidWhen: ["nothing has been said yet to reflect"],
    cognitiveLoadBudget: { expectedDemand: "low", mitigations: ["mirror, do not extend"] },
    validityRisks: ["putting words in the participant's mouth"],
    evidenceOutput: ["confirmed/corrected claim"],
    reviewAffordance: ["confirm", "correct", "reject"],
    prompt: "Here is what I think you meant. Did I capture it — in your words, not mine?",
  }),
  def({
    slug: "critical-incident-capture",
    name: "Critical incident capture",
    methodFamily: ["Critical Incident Technique"],
    constructTargets: ["real-events", "corrections"],
    interactionShape: "narrative",
    widget: "incident",
    bestFor: ["concrete incidents over abstract claims"],
    avoidWhen: ["no real incident exists to anchor on"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["one incident at a time", "structured slots"] },
    validityRisks: ["hindsight bias", "outcome bias"],
    evidenceOutput: ["structured incident record"],
    reviewAffordance: ["confirm", "correct", "defer", "redact"],
    prompt: "Walk me through one specific incident: the situation, what you did, and why.",
  }),
  def({
    slug: "laddering-probe",
    name: "Laddering probe",
    methodFamily: ["Means-End / Laddering"],
    constructTargets: ["terminal-value-behind-a-rule"],
    interactionShape: "narrative",
    widget: "ladder",
    bestFor: ["linking a surface rule to the value underneath it"],
    avoidWhen: ["the participant is fatigued — climbing is high-load"],
    cognitiveLoadBudget: { expectedDemand: "high", mitigations: ["stop at the terminal value", "one why at a time"] },
    validityRisks: ["rationalization", "articulated value ≠ enacted value"],
    evidenceOutput: ["value chain", "terminus"],
    reviewAffordance: ["confirm", "correct", "defer"],
    prompt: "Borderline cases should default to the customer. Why does that matter to you?",
  }),
  def({
    slug: "triadic-comparison",
    name: "Triadic comparison",
    methodFamily: ["Repertory Grid"],
    constructTargets: ["latent-distinction"],
    interactionShape: "comparison",
    widget: "triad",
    bestFor: ["eliciting a personal construct by contrasting three cases"],
    avoidWhen: ["fewer than three comparable cases exist"],
    cognitiveLoadBudget: { expectedDemand: "high", mitigations: ["only three cards", "participant names the pole"] },
    validityRisks: ["the analyst naming the construct for the participant"],
    evidenceOutput: ["bipolar construct", "poles"],
    reviewAffordance: ["confirm", "correct", "reframe"],
    prompt: "Two of these belong together and one stands apart. Which is the odd one out — and why?",
  }),
  def({
    slug: "best-worst-choice",
    name: "Best–worst choice",
    methodFamily: ["Best-Worst Scaling"],
    constructTargets: ["priority-conflict"],
    interactionShape: "ranking",
    widget: "bestworst",
    bestFor: ["forcing a priority when the participant wants to say 'both'"],
    avoidWhen: ["items are not genuinely in tension"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["one best, one worst", "recognition not recall"] },
    validityRisks: ["context-dependence read as a global ranking"],
    evidenceOutput: ["most/least signal"],
    reviewAffordance: ["confirm", "correct", "defer"],
    prompt: "In this borderline context, which do you protect most — and which least?",
  }),
  def({
    slug: "scenario-vignette",
    name: "Scenario vignette",
    methodFamily: ["Scenario", "Human-AI Interaction"],
    constructTargets: ["situated-decision", "edges"],
    interactionShape: "scenario",
    widget: "scenario",
    bestFor: ["a situated decision with a caveat that should travel with it"],
    avoidWhen: ["the scenario is too abstract to feel real"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["concrete vignette", "few options"] },
    validityRisks: ["hypothetical ≠ behavior under real stakes"],
    evidenceOutput: ["decision", "caveat"],
    reviewAffordance: ["confirm", "correct", "defer", "reframe"],
    prompt: "A documented defect, but the item shows heavy use. What should the agent do?",
  }),
  def({
    slug: "artifact-walkthrough",
    name: "Artifact walkthrough",
    methodFamily: ["Contextual Inquiry"],
    constructTargets: ["real-artifacts", "activity"],
    interactionShape: "walkthrough",
    widget: "narrative",
    bestFor: ["grounding elicitation in an authentic artifact"],
    avoidWhen: ["no artifact is available to walk through"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["follow the artifact, not a script"] },
    validityRisks: ["the artifact omitting tacit steps"],
    evidenceOutput: ["annotated artifact trace"],
    reviewAffordance: ["confirm", "correct", "redact"],
    prompt: "Show me a real example of this artifact and narrate what each part is for.",
  }),
  def({
    slug: "contextual-inquiry-prompt",
    name: "Contextual inquiry prompt",
    methodFamily: ["Contextual Inquiry"],
    constructTargets: ["tools", "handoffs"],
    interactionShape: "walkthrough",
    widget: "narrative",
    bestFor: ["connecting evidence to tools, handoffs, and operational context"],
    avoidWhen: ["the work has no observable activity"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["one handoff at a time"] },
    validityRisks: ["self-report drifting from observed practice"],
    evidenceOutput: ["workflow observation"],
    reviewAffordance: ["confirm", "correct", "defer"],
    prompt: "When this leaves your hands, where does it go next, and what could go wrong there?",
  }),
  def({
    slug: "card-sort",
    name: "Card sort",
    methodFamily: ["Card Sort"],
    constructTargets: ["category-structure", "authority-structure"],
    interactionShape: "sorting",
    widget: "cardsort",
    bestFor: ["a clean authority structure: what the agent decides vs escalates"],
    avoidWhen: ["the card set isn't representative of the real world"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["participant-named lanes", "few cards"] },
    validityRisks: ["card set ≠ exhaustive world"],
    evidenceOutput: ["categories", "membership"],
    reviewAffordance: ["confirm", "correct", "reframe"],
    prompt: "Sort these cases by who should decide them: the agent, a human, or escalation.",
  }),
  def({
    slug: "q-sort",
    name: "Q-sort",
    methodFamily: ["Q Methodology"],
    constructTargets: ["forced-distribution-of-priorities"],
    interactionShape: "sorting",
    widget: "qsort",
    bestFor: ["a forced distribution when everything can't be top priority"],
    avoidWhen: ["the participant is fatigued — high-load sorting"],
    cognitiveLoadBudget: { expectedDemand: "high", mitigations: ["fixed quota per column"] },
    validityRisks: ["forced distribution distorting genuine ties"],
    evidenceOutput: ["ranked distribution"],
    reviewAffordance: ["confirm", "correct", "defer"],
    prompt: "Place each statement on the grid — only a few may sit at 'most like me'.",
  }),
  def({
    slug: "semantic-differential",
    name: "Semantic differential",
    methodFamily: ["Semantic Differential"],
    constructTargets: ["posture-on-a-named-axis"],
    interactionShape: "scaling",
    widget: "differential",
    bestFor: ["a default tendency along a bipolar axis under uncertainty"],
    avoidWhen: ["the axis poles are not meaningful to the participant"],
    cognitiveLoadBudget: { expectedDemand: "low", mitigations: ["one axis", "explicit poles"] },
    validityRisks: ["a scalar read as a hard rule (false precision)"],
    evidenceOutput: ["scalar position"],
    reviewAffordance: ["confirm", "correct", "reframe"],
    prompt: "Under uncertainty, where does your default sit on this axis?",
  }),
  def({
    slug: "decision-reconstruction",
    name: "Decision reconstruction",
    methodFamily: ["Critical Decision Method"],
    constructTargets: ["expert-decision-steps"],
    interactionShape: "walkthrough",
    widget: "steps",
    bestFor: ["reconstructing a demanding expert decision step by step"],
    avoidWhen: ["the decision was routine, not demanding"],
    cognitiveLoadBudget: { expectedDemand: "high", mitigations: ["timeline scaffold", "one step at a time"] },
    validityRisks: ["retrospective coherence the moment didn't have"],
    evidenceOutput: ["decision step trace"],
    reviewAffordance: ["confirm", "correct", "defer"],
    prompt: "Reconstruct the toughest call. What were the cues, options, and the deciding factor?",
  }),
  def({
    slug: "self-explanation-prompt",
    name: "Self-explanation prompt",
    methodFamily: ["Self-Explanation"],
    constructTargets: ["rationale-behind-a-choice"],
    interactionShape: "narrative",
    widget: "narrative",
    bestFor: ["surfacing the reasoning behind a choice already made"],
    avoidWhen: ["no choice has been made to explain"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["explain, don't justify"] },
    validityRisks: ["post-hoc rationalization"],
    evidenceOutput: ["explanation record"],
    reviewAffordance: ["confirm", "correct", "reframe"],
    prompt: "You chose that option. Explain the reasoning to someone who'd do the opposite.",
  }),
  def({
    slug: "generative-canvas",
    name: "Generative canvas",
    methodFamily: ["Generative Elicitation", "Co-design"],
    constructTargets: ["structured-artifact", "shared-model"],
    interactionShape: "walkthrough",
    widget: "canvas",
    bestFor: ["co-building a structured artifact (a canvas, map, or model) when a fixed-option question would distort the topic"],
    avoidWhen: ["a single value, ranking, or narrative answers the question — reach for a focused primitive instead"],
    cognitiveLoadBudget: { expectedDemand: "medium", mitigations: ["seed the canvas so the participant edits rather than starts blank", "one region at a time"] },
    validityRisks: ["the seeded structure framing the answer (anchoring)", "fluency mistaken for completeness"],
    evidenceOutput: ["structured canvas artifact"],
    reviewAffordance: ["confirm", "correct", "reframe", "redact"],
    prompt: "Shape this canvas until it reflects how you actually see it — edit, add, or remove anything.",
  }),
  def({
    slug: "workload-check",
    name: "Workload check",
    methodFamily: ["Cognitive Load Theory"],
    constructTargets: ["current-cognitive-load"],
    interactionShape: "confirmation",
    widget: "workload",
    bestFor: ["budgeting working memory before choosing the next primitive"],
    avoidWhen: ["it would interrupt a productive flow"],
    cognitiveLoadBudget: { expectedDemand: "low", mitigations: ["one tap"] },
    validityRisks: ["under-reporting fatigue"],
    evidenceOutput: ["load state signal"],
    reviewAffordance: ["confirm", "defer"],
    prompt: "Quick check — how much is this asking of you right now?",
  }),
];

export const PRIMITIVE_BY_SLUG: Readonly<Record<string, PrimitiveDefinition>> = Object.fromEntries(
  PRIMITIVES.map((p) => [p.slug, p]),
);

export const PRIMITIVE_BY_URN: Readonly<Record<string, PrimitiveDefinition>> = Object.fromEntries(
  PRIMITIVES.map((p) => [p.primitiveId, p]),
);

/** German default prompts, so the adaptive interview reads well in `de` even offline. */
export const PROMPT_DE: Readonly<Record<string, string>> = {
  "open-narrative-prompt": "Erzählen Sie von einer Situation, in der die richtige Entscheidung nicht die einfache war. Was ist passiert?",
  "reflective-summary": "Hier ist, was ich verstanden habe. Habe ich es in Ihren Worten richtig erfasst?",
  "critical-incident-capture": "Schildern Sie einen konkreten Vorfall: die Situation, Ihr Vorgehen und warum.",
  "laddering-probe": "Grenzfälle sollten zugunsten der Kundin entschieden werden. Warum ist Ihnen das wichtig?",
  "triadic-comparison": "Zwei davon gehören zusammen, eines fällt heraus. Welches ist anders — und warum?",
  "best-worst-choice": "Was schützen Sie in diesem Grenzfall am meisten — und was am wenigsten?",
  "scenario-vignette": "Ein dokumentierter Defekt, aber das Produkt zeigt starke Nutzung. Was soll der Agent tun?",
  "artifact-walkthrough": "Zeigen Sie mir ein echtes Beispiel dieses Artefakts und erklären Sie, wozu jeder Teil dient.",
  "contextual-inquiry-prompt": "Wenn dies Ihre Hände verlässt — wohin geht es, und was könnte dort schiefgehen?",
  "card-sort": "Sortieren Sie diese Fälle danach, wer entscheiden soll: der Agent, ein Mensch oder Eskalation.",
  "q-sort": "Ordnen Sie jede Aussage im Raster an — nur wenige dürfen bei „trifft am meisten zu“ liegen.",
  "semantic-differential": "Wo liegt Ihre Tendenz unter Unsicherheit auf dieser Achse?",
  "decision-reconstruction": "Rekonstruieren Sie die schwierigste Entscheidung: Hinweise, Optionen und der ausschlaggebende Faktor.",
  "self-explanation-prompt": "Sie haben diese Option gewählt. Erklären Sie die Begründung jemandem, der das Gegenteil täte.",
  "generative-canvas": "Gestalten Sie diese Arbeitsfläche, bis sie Ihre Sicht widerspiegelt — bearbeiten, ergänzen oder entfernen Sie alles.",
  "workload-check": "Kurze Frage — wie viel verlangt Ihnen das gerade ab?",
};

export function localizedPrompt(p: PrimitiveDefinition, locale: string): string {
  return locale === "de" ? PROMPT_DE[p.slug] ?? p.prompt : p.prompt;
}
